import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN, pipe } from '@blade/std-lib'
import { useHapticFeedback } from '@blade/ui-kit'

import { GasSettingHeader } from './gas-settings-header'
import { GasSettingsRow } from './gas-settings-row'

import type { TransferGasSettingsReatom } from '../../../atoms/transfer-fee-reatom'
import type { SuggestedGasSettings } from '@root/entities/gas-information'

const TEMP_HIDE_ADVANCED = true

type GasSettingsTableProps = {
  model: TransferGasSettingsReatom
  onAdvanced?: () => void
}

export const GasSettingsTable: React.FC<GasSettingsTableProps> = ({
  model,
  onAdvanced,
}) => {
  const haptics = useHapticFeedback()
  const [gasInformationState] = useAtom(model.gasInformationStateAtom)
  const [gasSettings] = useAtom(model.selectedGasSettings)
  const [advancedSettings] = useAtom(model.advancedSettingsAtom)

  const setGasSettingsVariant = useAction(model.setGasSettingsVariantAction)

  const advancedSettingsState = useMemo(
    () => AsyncStateN.success(advancedSettings),
    [advancedSettings],
  )

  const renderRecommendedSettings = (
    variant: SuggestedGasSettings.SettingVariant,
  ) => {
    const state = pipe(
      gasInformationState,
      AsyncStateN.map((it) => it.variants[variant]),
    )
    return (
      <GasSettingsRow
        variant={variant}
        settingsState={state}
        onClick={() => {
          haptics.impactOccurred('light')
          setGasSettingsVariant(variant)
        }}
        selected={
          gasSettings.type === 'Recommended' && gasSettings.variant === variant
        }
      />
    )
  }

  return (
    <Root>
      <GasSettingHeader />
      {renderRecommendedSettings('low')}
      {renderRecommendedSettings('market')}
      {renderRecommendedSettings('high')}

      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
      {TEMP_HIDE_ADVANCED ? undefined : (
        <GasSettingsRow
          onClick={() => {
            onAdvanced?.()
          }}
          variant="advanced"
          settingsState={advancedSettingsState}
          selected={gasSettings.type === 'Advanced'}
        />
      )}
    </Root>
  )
}

const Root = styled.div`
  margin-bottom: 24px;
`
